import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Settings2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api, errorMessage } from "@/lib/api";
import { MONTHS, SERVICE_TYPES } from "@/lib/services";
import { CONTACT_STATUS } from "@/lib/contact";
import { useAuth } from "@/context/AuthContext";
import { PlanningItem } from "@/components/app/PlanningItem";
import { ContactDialog } from "@/components/app/ContactDialog";
import { MessagingDialog } from "@/components/app/MessagingDialog";

const ALL = "todos";
const CONTACT_OPTIONS = [[ALL, "Toda situação"], ...Object.entries(CONTACT_STATUS).map(([k, [l]]) => [k, l])];

const Pick = ({ value, onChange, options, testid, placeholder }) => (
  <Select value={value} onValueChange={onChange}>
    <SelectTrigger className="h-10 w-full bg-slate-900 sm:w-48" data-testid={testid}><SelectValue placeholder={placeholder} /></SelectTrigger>
    <SelectContent className="border-slate-700 bg-slate-900">
      {options.map(([k, label]) => <SelectItem key={k} value={k} data-testid={`${testid}-${k}`}>{label}</SelectItem>)}
    </SelectContent>
  </Select>
);

const Group = ({ title, items, empty, testid, onAction, onChanged }) => (
  <section data-testid={testid}>
    <h2 className="mb-3 font-display text-lg font-bold">{title} <span className="font-mono text-sm text-slate-500">({items.length})</span></h2>
    {items.length === 0 ? <p className="rounded-xl border border-dashed border-slate-700 py-10 text-center text-sm text-slate-500" data-testid={`${testid}-empty`}>{empty}</p> : (
      <ul className="grid gap-3 lg:grid-cols-2">{items.map((s) => <PlanningItem key={s.id} s={s} onAction={onAction} onChanged={onChanged} />)}</ul>
    )}
  </section>
);

export const PlanningView = ({ version, onChanged }) => {
  const { user } = useAuth();
  const [month, setMonth] = useState("");
  const [data, setData] = useState(null);
  const [f, setF] = useState({ q: "", type: ALL, status: ALL });
  const [dialog, setDialog] = useState(null);
  const [settings, setSettings] = useState(false);
  const [tick, setTick] = useState(0);

  const refresh = useCallback(() => { setTick((t) => t + 1); onChanged(); }, [onChanged]);
  useEffect(() => {
    api.get("/planning", { params: month ? { month } : {} }).then((r) => { setData(r.data); if (!month) setMonth(r.data.month); })
      .catch((e) => toast.error(errorMessage(e)));
  }, [month, version, tick]);

  const match = (s) => s.client_name.toLowerCase().includes(f.q.trim().toLowerCase())
    && (f.type === ALL || s.service_type === f.type) && (f.status === ALL || s.contact_status === f.status);
  const label = data ? `${MONTHS[Number(data.month.slice(5, 7)) - 1]} ${data.month.slice(0, 4)}` : "";
  const onAction = (item, status) => setDialog({ item, status });

  return (
    <div className="space-y-6" data-testid="planning-view">
      <div className="flex flex-col gap-3 rounded-xl border border-slate-700/60 bg-slate-900/60 p-4 lg:flex-row lg:items-end lg:justify-between">
        <div>
          <h2 className="font-display text-lg font-bold">Planejamento de contatos</h2>
          <p className="text-xs text-slate-400">Todo dia {data?.planning_day || 25}, o Ciclo Certo prepara a lista do mês seguinte. Os atrasados ficam sempre visíveis.</p>
        </div>
        <div className="flex flex-col gap-2 sm:flex-row sm:flex-wrap">
          <Input type="month" value={month} onChange={(e) => e.target.value && setMonth(e.target.value)} className="h-10 bg-slate-900 sm:w-44" data-testid="planning-month-input" aria-label="Mês" />
          <Input value={f.q} onChange={(e) => setF({ ...f, q: e.target.value })} placeholder="Buscar cliente" className="h-10 bg-slate-900 sm:w-44" data-testid="planning-search-input" />
          <Pick value={f.type} onChange={(type) => setF({ ...f, type })} testid="planning-type-filter" options={[[ALL, "Todos os serviços"], ...Object.entries(SERVICE_TYPES).map(([k, t]) => [k, t.label])]} />
          <Pick value={f.status} onChange={(status) => setF({ ...f, status })} testid="planning-status-filter" options={CONTACT_OPTIONS} />
          {user?.tenant_role === "owner" && <Button variant="outline" onClick={() => setSettings(true)} className="h-10 rounded-full border-slate-700 bg-transparent" data-testid="planning-settings-button"><Settings2 className="mr-1 h-4 w-4" /> Mensagens</Button>}
        </div>
      </div>
      {data && (
        <>
          <Group title="Atrasados" items={data.overdue.filter(match)} empty="Nenhum serviço atrasado." testid="planning-overdue" onAction={onAction} onChanged={refresh} />
          <Group title={`Previstos para ${label}`} items={data.items.filter(match)} empty="Nenhum serviço previsto para este mês." testid="planning-month" onAction={onAction} onChanged={refresh} />
        </>
      )}
      <ContactDialog state={dialog} onClose={() => setDialog(null)} onSaved={refresh} />
      <MessagingDialog open={settings} onClose={() => setSettings(false)} onSaved={onChanged} />
    </div>
  );
};
